import { useCallback, useEffect, useState } from "react";

export interface SavedCaseView {
  id: string;
  label: string;
  /** URLSearchParams string (without leading "?"). */
  search: string;
}

const STORAGE_KEY = "ac_cases_saved_views";

function read(): SavedCaseView[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is SavedCaseView =>
        v &&
        typeof v.id === "string" &&
        typeof v.label === "string" &&
        typeof v.search === "string",
    );
  } catch {
    return [];
  }
}

function write(views: SavedCaseView[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * localStorage-backed registry of user-saved case views.
 * Syncs across mounts via the browser `storage` event.
 */
export function useSavedCaseViews() {
  const [views, setViews] = useState<SavedCaseView[]>(() => read());

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setViews(read());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Subscribe to local-window writes too (storage event only fires across tabs).
  useEffect(() => {
    const handler = () => setViews(read());
    window.addEventListener("ac:saved-views-changed", handler);
    return () => window.removeEventListener("ac:saved-views-changed", handler);
  }, []);

  const addView = useCallback(
    (label: string, search: string): SavedCaseView => {
      const id = `sv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      const next: SavedCaseView = {
        id,
        label: label.trim().slice(0, 60) || "Saved view",
        search: search.replace(/^\?/, ""),
      };
      const all = [...read(), next];
      write(all);
      setViews(all);
      window.dispatchEvent(new Event("ac:saved-views-changed"));
      return next;
    },
    [],
  );

  const removeView = useCallback((id: string) => {
    const all = read().filter((v) => v.id !== id);
    write(all);
    setViews(all);
    window.dispatchEvent(new Event("ac:saved-views-changed"));
  }, []);

  return { views, addView, removeView };
}
